import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import countryToCurrency from 'country-to-currency';

countries.registerLocale(enLocale);

/**
 * Normalize a whois address country value to ISO alpha-2.
 * Handles alpha-2, alpha-3, and full country names.
 */
const toAlpha2 = (countryRaw) => {
  if (countryRaw == null) return null;
  const trimmed = String(countryRaw).trim();
  if (!trimmed) return null;

  const upper = trimmed.toUpperCase();

  if (countries.isValid(upper)) {
    return upper;
  }

  if (upper.length === 3) {
    const fromAlpha3 = countries.alpha3ToAlpha2(upper);
    if (fromAlpha3) return fromAlpha3;
  }

  const fromName = countries.getAlpha2Code(trimmed, 'en');
  return fromName || null;
};

/**
 * Last comma-separated segment of a formatted address (e.g. "..., CA" -> "CA").
 * @returns {string|null}
 */
export const extractCountryFromAddressLine = (addressLine) => {
  if (addressLine == null) return null;
  const trimmed = String(addressLine).trim();
  if (!trimmed) return null;
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  return parts[parts.length - 1];
};

/**
 * Resolve ISO 4217 currency code from a country value (alpha-2, alpha-3, or name).
 * @returns {string|null}
 */
export const resolveCurrencyFromCountry = (countryRaw) => {
  const alpha2 = toAlpha2(countryRaw);
  if (!alpha2) return null;

  const currency = countryToCurrency[alpha2];
  return currency && String(currency).trim() ? String(currency).trim() : null;
};

/**
 * Resolve currency from whois address object (country field or trailing address segment).
 * @returns {string|null}
 */
export const resolveCurrencyFromWhoisAddress = (address) => {
  if (!address) return null;

  const fromCountry = resolveCurrencyFromCountry(address.country);
  if (fromCountry) return fromCountry;

  const addressLine = [
    address.address1,
    address.address2,
    address.city,
    address.stateProv,
    address.postalCode,
    address.country,
  ]
    .filter((p) => p && String(p).trim())
    .join(', ');

  const trailing = extractCountryFromAddressLine(addressLine);
  return resolveCurrencyFromCountry(trailing);
};
